import React from 'react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';

export const AboutPage: React.FC = () => {
  return (
    <main className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              About <span className="text-green-600">Farmero</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connecting Nepali farmers and artisans with consumers through authentic organic products from the Himalayas
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-4">
                Founded in the heart of Nepal, Farmero bridges the gap between Himalayan farmers, traditional artisans, and conscious consumers worldwide. We believe in the power of authentic Nepali organic products - from honey and tea to handcrafted textiles - to preserve our heritage while supporting sustainable livelihoods.
              </p>
              <p className="text-gray-600 mb-6">
                Our platform empowers Nepali farmers and artisans to reach wider markets while ensuring consumers have access to genuine, highest-quality products directly from the Himalayan valleys and traditional workshops of Nepal.
              </p>
              <div className="flex space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">300+</div>
                  <div className="text-sm text-gray-500">Nepali Farmers & Artisans</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">5k+</div>
                  <div className="text-sm text-gray-500">Happy Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">25+</div>
                  <div className="text-sm text-gray-500">Districts in Nepal</div>
                </div>
              </div>
            </div>
            <div className="bg-green-100 rounded-2xl p-8">
              <div className="w-full h-64 bg-green-200 rounded-xl flex items-center justify-center">
                <span className="text-green-600 text-lg font-medium">Farm Image Placeholder</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Everything we do is guided by our commitment to sustainability, authenticity, and supporting Nepali communities
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-xl shadow-sm">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Sustainability</h3>
              <p className="text-gray-600">
                Promoting traditional eco-friendly farming practices in the Himalayas that protect our mountains and valleys for future generations
              </p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-xl shadow-sm">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Authenticity</h3>
              <p className="text-gray-600">
                Ensuring every product is genuinely sourced from Nepal, meeting the highest standards of quality and preserving traditional craftsmanship
              </p>
            </div>
            
            <div className="text-center p-6 bg-white rounded-xl shadow-sm">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Community</h3>
              <p className="text-gray-600">
                Building strong relationships between Nepali farmers, artisans, consumers, and supporting local communities across the Himalayas
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Passionate Nepali team working together to showcase authentic Himalayan products to the world
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-gray-400">Photo</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Rajesh Shrestha</h3>
              <p className="text-green-600 mb-2">Founder & CEO</p>
              <p className="text-gray-600 text-sm">
                Agricultural expert with 15 years of experience in sustainable Himalayan farming and connecting local producers to global markets
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-gray-400">Photo</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Sita Gurung</h3>
              <p className="text-green-600 mb-2">Head of Operations</p>
              <p className="text-gray-600 text-sm">
                Expert in supply chain management and building strong relationships with farmers and artisans across Nepal's diverse regions
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-gray-400">Photo</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1">Pratima Karki</h3>
              <p className="text-green-600 mb-2">Customer Experience Director</p>
              <p className="text-gray-600 text-sm">
                Dedicated to ensuring every customer experiences the authentic quality and cultural richness of Nepal's finest organic products
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};
